import React, { useRef, useState } from "react";
import { PageWrapper } from "../material-ledger-summary";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { NavLink } from "react-router-dom";
import useNotification from "../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { MLAppConfig } from "../../ml-models/MLAppConfig";
import { useApi } from "../../../../shared/hooks/useApi";

const ACCEPTED_TYPES = ".xlsx,.xls";
const MAX_SIZE_MB = 10;

interface AllowedFacility {
  originFacilityTeId: string;
  country: string;
  state: string;
}
interface LotIssues {
  lotNo: string;
  issues: string[];
}
/** Structured SC-validation failure (origin facility / country-state mismatches). */
interface ScErrorDetail {
  summary?: string;
  scNo?: string;
  generalIssues?: string[];
  errorsByLot?: LotIssues[];
  allowedFacilities?: AllowedFacility[];
}
interface FpfErrorLogEntry {
  timestamp: string;
  fileName: string;
  fileSizeKB: string;
  stage: "client-validation" | "server-validation" | "network";
  errors: string[];          // flat list (schema/format errors + flattened SC errors)
  sc?: ScErrorDetail;        // present when the failure is an SC-relationship mismatch
}

/** Split the backend's "err1; err2 | Row 15: err3" strings into list items. */
const splitServerErrors = (raw: string): string[] =>
  raw
    .split(/\s*\|\s*|;\s*(?=[A-Z(])/g)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Normalise the backend error body into flat lines + optional structured SC data.
 * Handles three shapes: the structured SC response (errorsByLot/allowedFacilities),
 * a plain {response|message} string, and a {errors:[...]} list.
 */
const parseServerError = (data: any): { errors: string[]; sc?: ScErrorDetail } => {
  if (data && (Array.isArray(data.errorsByLot) || Array.isArray(data.allowedFacilities))) {
    const sc: ScErrorDetail = {
      summary: data.response || data.summary,
      scNo: data.scNo,
      generalIssues: data.generalIssues || [],
      errorsByLot: data.errorsByLot || [],
      allowedFacilities: data.allowedFacilities || [],
    };
    const flat: string[] = [...(sc.generalIssues || [])];
    (sc.errorsByLot || []).forEach((lot) =>
      lot.issues.forEach((iss) => flat.push(`Lot '${lot.lotNo}': ${iss}`))
    );
    return { errors: flat.length ? flat : [sc.summary || "SC validation failed."], sc };
  }
  if (data && Array.isArray(data.errors)) return { errors: data.errors };
  const msg: string = data?.response || data?.message || "Upload failed. Please check the file and try again.";
  return { errors: splitServerErrors(String(msg)) };
};

const FpfForm: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorLog, setErrorLog] = useState<FpfErrorLogEntry[]>([]);
  // After a failed upload the user must Clear or choose a new file before retrying
  const [uploadBlocked, setUploadBlocked] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { cbLicensingCode } = useTransactionCertificateStore();
  const { notify } = useNotification();
  const { executePut } = useApi();

  const latestEntry = errorLog.length > 0 ? errorLog[0] : null;

  const pushErrorLog = (
    stage: FpfErrorLogEntry["stage"],
    errors: string[],
    file: File | null,
    sc?: ScErrorDetail
  ) => {
    setErrorLog((prev) => [
      {
        timestamp: new Date().toISOString(),
        fileName: file?.name ?? "(no file)",
        fileSizeKB: file ? (file.size / 1024).toFixed(1) : "0",
        stage,
        errors,
        sc,
      },
      ...prev,
    ]);
  };

  const handleDownloadErrorLog = () => {
    const lines: string[] = [
      "FPF UPLOAD — ERROR LOG",
      `Generated : ${new Date().toLocaleString()}`,
      `CB Code   : ${cbLicensingCode || "(unknown)"}`,
      "=".repeat(70),
      "",
    ];
    errorLog.forEach((entry, i) => {
      lines.push(
        `[Attempt ${errorLog.length - i}] ${new Date(entry.timestamp).toLocaleString()}`,
        `File  : ${entry.fileName} (${entry.fileSizeKB} KB)`,
        `Stage : ${entry.stage}`,
        "Errors:"
      );
      entry.errors.forEach((err, j) => lines.push(`  ${j + 1}. ${err}`));
      if (entry.sc?.allowedFacilities?.length) {
        lines.push("", "Allowed on this SC (origin facility / country / state):");
        entry.sc.allowedFacilities.forEach((f) =>
          lines.push(`  - ${f.originFacilityTeId}  ${f.country}/${f.state}`)
        );
      }
      lines.push("-".repeat(70), "");
    });

    const blob = new Blob([lines.join("\r\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fpf-upload-errors-${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadBlocked(false); // a new file selection lifts the error block
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    const errors: string[] = [];
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      errors.push("Only .xlsx and .xls files are accepted.");
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      errors.push(`File size must be under ${MAX_SIZE_MB} MB.`);
    }

    if (errors.length > 0) {
      pushErrorLog("client-validation", errors, file);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      notify("Please select a file first.", "error");
      return;
    }
    if (!cbLicensingCode) {
      notify("CB Licensing Code not available. Please log in again.", "error");
      return;
    }

    setUploading(true);

    try {
      const fileBytes = await selectedFile.arrayBuffer();
      const url = `${MLAppConfig.UPLOAD_FPF_EXCEL}?filename=${encodeURIComponent(selectedFile.name)}&tcCbLicensingCode=${encodeURIComponent(cbLicensingCode)}`;

      const response = await executePut(url, fileBytes, {
        headers: { "Content-Type": "application/octet-stream" },
      });

      if (response?.status === 200) {
        const okMsg = (response?.data as any)?.response
          || "FPF file uploaded successfully. Processing has started.";
        notify(okMsg, "success");
        setErrorLog([]); // success — clear any stale validation errors from prior attempts
        handleReset();   // clear the form so the user can upload another
        return;
      }

      // useApi returns {data, status} for 400/500 instead of throwing.
      const { errors, sc } = parseServerError(response?.data);
      pushErrorLog("server-validation", errors, selectedFile, sc);
      setUploadBlocked(true);
      notify("Upload failed. See validation errors below.", "error");
    } catch (err: any) {
      const errData = err?.response?.data;
      if (errData && (Array.isArray(errData.errorsByLot) || Array.isArray(errData.errors) || errData.response)) {
        const { errors, sc } = parseServerError(errData);
        pushErrorLog("server-validation", errors, selectedFile, sc);
      } else {
        pushErrorLog("network", [err?.message || "Network error. Could not reach the FPF upload service."], selectedFile);
      }
      setUploadBlocked(true);
      notify("Upload failed. See errors below.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadBlocked(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <PageWrapper>
      <Typography mt={4} variant="h6" gutterBottom align="left" fontWeight="600">
        FPF Upload — Farm Production Form
      </Typography>

      <Box
        sx={{
          mt: 3,
          p: 4,
          border: "2px dashed #ccc",
          borderRadius: 2,
          maxWidth: 720,
          backgroundColor: "#fafafa",
        }}
      >
        <Typography variant="body1" mb={2} color="text.secondary">
          Upload the completed FPF Excel template (.xlsx / .xls). The file will be
          validated and processed automatically once uploaded.
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="fpf-file-input"
          />
          <StyledButton
            variant="contained"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose File
          </StyledButton>
          {selectedFile && (
            <Typography variant="body2" color="text.primary" sx={{ flexGrow: 1, wordBreak: "break-all" }}>
              {selectedFile.name}{" "}
              <span style={{ color: "#888" }}>({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            </Typography>
          )}
        </Box>

        {selectedFile && !uploading && (
          <Box sx={{ display: "flex", gap: 2, mt: 3, alignItems: "center", flexWrap: "wrap" }}>
            <StyledButton variant="contained" onClick={handleUpload} disabled={uploadBlocked}>
              Upload &amp; Submit
            </StyledButton>
            <StyledButton variant="outlined" onClick={handleReset}>
              Clear
            </StyledButton>
            {uploadBlocked && (
              <Typography variant="caption" color="error">
                Fix the errors below, then Clear or choose a corrected file to upload again.
              </Typography>
            )}
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Validating and uploading…
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {latestEntry && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "#fff3f3",
              border: "1px solid #f5c6cb",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" fontWeight={600} color="error">
                Validation errors ({latestEntry.errors.length})
              </Typography>
              <StyledButton variant="outlined" size="small" onClick={handleDownloadErrorLog}>
                Download Error Log
              </StyledButton>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {new Date(latestEntry.timestamp).toLocaleString()} — {latestEntry.fileName} ({latestEntry.fileSizeKB} KB) — {latestEntry.stage}
            </Typography>

            {latestEntry.sc ? (
              <>
                {latestEntry.sc.summary && (
                  <Typography variant="body2" color="error" mb={1.5}>
                    {latestEntry.sc.summary}
                  </Typography>
                )}

                {!!latestEntry.sc.generalIssues?.length && (
                  <Box sx={{ mb: 1.5 }}>
                    {latestEntry.sc.generalIssues.map((g, i) => (
                      <Typography key={i} variant="body2" color="error">• {g}</Typography>
                    ))}
                  </Box>
                )}

                {latestEntry.sc.errorsByLot?.map((lot, i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Lot: {lot.lotNo}
                    </Typography>
                    {lot.issues.map((iss, j) => (
                      <Typography key={j} variant="body2" color="error" sx={{ pl: 1.5 }}>
                        • {iss}
                      </Typography>
                    ))}
                  </Box>
                ))}

                {!!latestEntry.sc.allowedFacilities?.length && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary" mb={0.5}>
                      Allowed on this SC{latestEntry.sc.scNo ? ` (${latestEntry.sc.scNo})` : ""}:
                    </Typography>
                    <TableContainer sx={{ maxWidth: 460, border: "1px solid #e0e0e0", borderRadius: 1, backgroundColor: "#fff" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Origin Facility TE-ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>State/Province</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {latestEntry.sc.allowedFacilities.map((f, i) => (
                            <TableRow key={i}>
                              <TableCell>{f.originFacilityTeId}</TableCell>
                              <TableCell>{f.country}</TableCell>
                              <TableCell>{f.state}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            ) : (
              latestEntry.errors.map((err, i) => (
                <Typography key={i} variant="body2" color="error">
                  • {err}
                </Typography>
              ))
            )}
          </Box>
        )}

        {errorLog.length > 1 && (
          <Accordion sx={{ mt: 2, backgroundColor: "transparent" }} elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                Previous attempts ({errorLog.length - 1})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {errorLog.slice(1).map((entry, i) => (
                <Box key={i} sx={{ mb: 2, pb: 1, borderBottom: "1px solid #eee" }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.timestamp).toLocaleString()} — {entry.fileName} ({entry.fileSizeKB} KB) — {entry.stage}
                  </Typography>
                  {entry.errors.map((err, j) => (
                    <Typography key={j} variant="body2" color="error">
                      • {err}
                    </Typography>
                  ))}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 3, gap: 2 }}>
        <NavLink to="/">
          <StyledButton variant="contained">Cancel</StyledButton>
        </NavLink>
      </Box>
    </PageWrapper>
  );
};

export default FpfForm;
